import { useSearchParams } from 'react-router-dom';
import { useUrlState, codecs, type UrlCodec } from '../../hooks/useUrlState';
import { resolveDatePreset } from '../../utils/date-presets';
import type { DateRangeValue } from '../../utils/date-presets';

export type AdsLevel = 'campaign' | 'adset' | 'ad';

export const LEVELS: { key: AdsLevel; label: string }[] = [
  { key: 'campaign', label: 'Campaigns' },
  { key: 'adset', label: 'Ad Sets' },
  { key: 'ad', label: 'Ads' },
];

/** A ?tab= value from an old or hand-edited link falls back rather than rendering nothing. */
const levelCodec: UrlCodec<AdsLevel> = {
  encode: (v) => v,
  decode: (s) => (LEVELS.some((l) => l.key === s) ? (s as AdsLevel) : 'campaign'),
};

/** Tab plus the selection scoping it, all in the URL so a link reproduces the view. */
export function useAdsManagerState() {
  const [, setSearchParams] = useSearchParams();
  const [dateRange, setDateRange] = useUrlState<DateRangeValue>(
    'dates', resolveDatePreset('last_7_days'), codecs.dateRange, 'push',
  );
  const [adAccountId, setAdAccountId] = useUrlState('account', '', codecs.str, 'push');
  const [level, setLevel] = useUrlState<AdsLevel>('tab', 'campaign', levelCodec, 'push');

  // Changing the campaign selection invalidates any ad-set selection made under it.
  const [campaignIds, setCampaignIds] = useUrlState<string[]>('campaigns', [], codecs.csv, 'push', ['adsets']);
  const [adsetIds, setAdsetIds] = useUrlState<string[]>('adsets', [], codecs.csv, 'push');
  const [includeArchived, setIncludeArchived] = useUrlState('archived', false, codecs.bool, 'push');

  // One write: two useUrlState updates in the same tick do not compose, and the second wins.
  const drillInto = (next: AdsLevel, selected: string[]) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.set('tab', next);
      if (level === 'campaign' && next !== 'campaign') {
        selected.length ? sp.set('campaigns', selected.join(',')) : sp.delete('campaigns');
        sp.delete('adsets');
      }
      if (level === 'adset' && next === 'ad') {
        selected.length ? sp.set('adsets', selected.join(',')) : sp.delete('adsets');
      }
      return sp;
    });
  };

  // One write, as drillInto is: two useUrlState updates in a tick do not compose.
  const clearSelection = () => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      sp.delete('campaigns');
      sp.delete('adsets');
      return sp;
    });
  };

  const fmt = (d: Date | null) => (d ? new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10) : '');

  return {
    dateRange, setDateRange,
    adAccountId, setAdAccountId,
    level, setLevel,
    campaignIds, setCampaignIds,
    adsetIds, setAdsetIds,
    includeArchived, setIncludeArchived,
    drillInto,
    clearSelection,
    filters: {
      date_from: fmt(dateRange[0]),
      date_to: fmt(dateRange[1]),
      level,
      ad_account_id: adAccountId || undefined,
      campaign_ids: campaignIds.length ? campaignIds : undefined,
      adset_ids: adsetIds.length ? adsetIds : undefined,
      include_archived: includeArchived || undefined,
    },
  };
}
